import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Grid, Paper, useMediaQuery } from "@mui/material";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTheme } from "@mui/material/styles";

const KitchenDocuments = ({ editMode, kitchenData, updateKitchenData }) => {
  const [documentData, setDocumentData] = useState({
    liabilityInsurance: { file: null, preview: "", url: "" },
    healthCertificate: { file: null, preview: "", url: "" },
    businessLicense: { file: null, preview: "", url: "" },
  });

  const fileInputRefs = {
    liabilityInsurance: useRef(null),
    healthCertificate: useRef(null),
    businessLicense: useRef(null),
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (editMode && kitchenData.documents) {
      setDocumentData({
        liabilityInsurance: kitchenData.documents.liabilityInsurance || {
          file: null,
          preview: "",
          url: "",
        },
        healthCertificate: kitchenData.documents.healthCertificate || {
          file: null,
          preview: "",
          url: "",
        },
        businessLicense: kitchenData.documents.businessLicense || {
          file: null,
          preview: "",
          url: "",
        },
      });
    }
  }, [editMode, kitchenData.documents]);

  const handleFileChange = (docType, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    const updatedDoc = { file, preview: previewUrl, url: "" };
    const newDocumentData = { ...documentData, [docType]: updatedDoc };
    setDocumentData(newDocumentData);
    updateKitchenData({ documents: newDocumentData });
  };

  const handleUploadClick = (docType) => {
    fileInputRefs[docType].current.click();
  };

  const renderPreview = (docObj, label) => {
    const isImage =
      docObj?.file?.type?.startsWith("image/") ||
      /\.(jpg|jpeg|png)$/i.test(docObj?.url);

    if (docObj.file || docObj.url) {
      return (
        <>
          {isImage ? (
            <Box
              component="img"
              src={docObj.preview || docObj.url}
              alt={label}
              sx={{
                width: "100%",
                height: 120,
                objectFit: "cover",
                borderRadius: 1,
                mb: 1,
              }}
            />
          ) : (
            <Typography fontSize="0.875rem" fontWeight="medium" color="primary">
              {docObj.file?.name || "View Uploaded File"}
            </Typography>
          )}
        </>
      );
    }

    return (
      <Box
        sx={{
          width: 60,
          height: 60,
          backgroundColor: "#eee",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <FaCloudUploadAlt size={30} color="#aaa" />
      </Box>
    );
  };

  const docFields = [
    { key: "liabilityInsurance", label: "Liability Insurance" },
    { key: "healthCertificate", label: "Health Certificate" },
    { key: "businessLicense", label: "Business License" },
  ];

  return (
    <Box
      sx={{
        p: isMobile ? 1 : 4,
        background: "#fff",
      }}
    >
      <Typography variant="h6" gutterBottom>
        Upload Documents
      </Typography>

      <Grid container spacing={3} justifyContent="center">
        {docFields.map(({ key, label }) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={key}
            onClick={() => handleUploadClick(key)}
          >
            <Paper
              elevation={3}
              sx={{
                height: 220,
                border: "2px dashed #ccc",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                padding: 2,
                cursor: "pointer",
                transition: "0.3s ease",
                textAlign: "center",
                "&:hover": {
                  borderColor: "#333",
                  backgroundColor: "#f9f9f9",
                },
              }}
            >
              {renderPreview(documentData[key], label)}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {label}
              </Typography>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                ref={fileInputRefs[key]}
                onChange={(e) => handleFileChange(key, e)}
                hidden
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default KitchenDocuments;
